import { CookiesStorageService } from '@/services/cookiesStorageService';
import { getUrlParams } from '@/helpers/getUrlParams';
import { isTier1Geo } from '@/helpers/isTier1Geo';
import { COOKIES_KEYS, MONETIZATION_TEST } from '@/constants';

type MonetizationTestValue =
	(typeof MONETIZATION_TEST)[keyof typeof MONETIZATION_TEST];

const MONETIZATION_TESTS: readonly string[] = Object.values(MONETIZATION_TEST);

// The `m_test` value this visitor carries, from the URL param or the cookie it
// was persisted into (see `usePersistTestParams`), so it survives on pages
// opened without the param. Says nothing about geo.
const getMonetizationTestValue = (): MonetizationTestValue | null => {
	const { m_test: paramValue } = getUrlParams();
	const value =
		paramValue || CookiesStorageService.get<string>(COOKIES_KEYS.M_TEST, '');

	return MONETIZATION_TESTS.includes(value)
		? (value as MonetizationTestValue)
		: null;
};

/**
 * Whether the visitor is in a monetization-test cohort at all, before geo is
 * taken into account.
 *
 * `getMonetizationTest` can only answer once geo has resolved, and geo is
 * looked up asynchronously. A page that must not render the wrong variant
 * meanwhile uses this to tell "no test, the default flow for sure" from
 * "a test, but the geo gate hasn't answered yet" — only the latter has to wait.
 */
export function hasMonetizationTest(): boolean {
	return getMonetizationTestValue() !== null;
}

/**
 * Active monetization test for this visitor, or `null` for the default flow.
 *
 * Mirrors the web app's `getMTestVariant`: the tests only ever target
 * non-Tier-1 geos.
 *
 * Client-side only — geo lives in localStorage, so callers must read it after
 * mount to keep SSR markup (and the CDN cache) variant-free.
 */
export function getMonetizationTest(): MonetizationTestValue | null {
	if (isTier1Geo()) return null;

	return getMonetizationTestValue();
}
